import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyCYjy1y-ZXwNfiv_a1DgzbSxCSA7fNoO64",
  authDomain: "flying-blind-app.firebaseapp.com",
  projectId: "flying-blind-app",
  storageBucket: "flying-blind-app.appspot.com",
  messagingSenderId: "212123355293",
  appId: "1:212123355293:web:08c15b637ad762fa2f2c74",
  measurementId: "G-3QF0WXQWT7"
};

// --- App Data & Structure ---
const ASSESSMENT_DATA = [
    { id: 'stock', title: 'Taking Honest Stock', prompts: [
        { id: 'energy', text: 'On a scale of 1-10, what is your personal energy level at the end of a typical workday?', type: 'slider', points: (val) => 10 - parseInt(val, 10) },
        { id: 'drains', text: 'What are the three biggest drains on your energy right now? Name them specifically.', type: 'textarea', points: (val) => val.trim() !== '' ? 5 : 0 },
        { id: 'pillars', text: 'What are the core pillars of your life? (e.g., my son, my health, my music, my mission). List yours.', type: 'textarea', points: (val) => val.trim() !== '' ? 0 : 5 },
        { id: 'neglected', text: 'Looking at your list of pillars, which one is the most neglected right now?', type: 'textarea', points: (val) => val.trim() !== '' ? 5 : 0 }
    ]},
    { id: 'blowtorch', title: 'The Blowtorch & The Match', prompts: [
        { id: 'match', text: 'What is your "match"? What\'s the one thing you do that consumes the most energy for the least fulfillment?', type: 'textarea', points: (val) => val.trim() !== '' ? 5 : 0 },
        { id: 'blowtorch', text: 'Now, what is your "blowtorch"? What skills or passions do you possess that make you feel powerful?', type: 'textarea', points: (val) => val.trim() !== '' ? 0 : 5 },
        { id: 'fear', text: 'What is the biggest fear that stops you from putting down the match and picking up the blowtorch?', type: 'textarea', points: (val) => val.trim() !== '' ? 5 : 0 }
    ]},
    { id: 'silence', title: 'Navigating The Silence', prompts: [
        { id: 'unheard', text: 'Describe a time you reached out for help and felt unheard or dismissed.', type: 'textarea', points: (val) => val.trim() !== '' ? 5 : 0 },
        { id: 'quiet_kid', text: 'Who is the "quiet kid on the playground" inside you? What does he truly want to say?', type: 'textarea', points: (val) => val.trim() !== '' ? 5 : 0 },
        { id: 'letter', text: 'Write a short, honest letter to one person you wish understood you better. You don\'t have to send it.', type: 'textarea', points: (val) => val.trim() !== '' ? 0 : 5 }
    ]},
    { id: 'escape', title: 'The "No Escape Hatch"', prompts: [
        { id: 'hatch', text: 'What was your old "escape hatch"? Your go-to method for numbing difficult feelings?', type: 'textarea', points: (val) => val.trim() !== '' ? 5 : 0 },
        { id: 'coping', text: 'What is one healthy coping mechanism you can use the next time you feel that immense pressure?', type: 'textarea', points: (val) => val.trim() !== '' ? 0 : 5 }
    ]},
    { id: 'path', title: 'Defining A New Path', prompts: [
        { id: 'want_more', text: 'Based on everything you\'ve written, what is ONE thing you want more of in your life right now?', type: 'textarea', points: (val) => val.trim() !== '' ? 0 : 5 },
        { id: 'action_step', text: 'What is ONE small, tangible action you can take in the next 7 days that moves you 1% closer to that thing?', type: 'textarea', points: (val) => val.trim() !== '' ? 0 : 5 }
    ]}
];

// --- Helper Components ---
const AppLayout = ({ children }) => (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#111] p-4 font-sans text-gray-200">
        <div className="w-full h-full max-w-md bg-black md:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-800" style={{minHeight: '90vh'}}>
            {children}
        </div>
    </div>
);

const WelcomeView = ({ onBegin }) => (
    <div className="flex-grow flex flex-col justify-center items-center text-center p-8 animate-fadeIn">
        <img src="https://images.squarespace-cdn.com/content/v1/6735a07287515277ab6de003/ff00d763-30d0-4a42-b8e7-7e4864a134e0/IOH_Logo_COLOR+Option5.png?format=1500w" alt="I'm Out Here Logo" className="w-32 h-32 mx-auto mb-6" />
        <h1 className="font-display font-black text-4xl text-white">Flying Blind</h1>
        <p className="text-lg text-gray-400 mt-2 mb-8">An Assessment for Building Your Own Track</p>
        <button onClick={onBegin} className="btn btn-primary">Begin Assessment</button>
    </div>
);

const AssessmentView = ({ onComplete }) => {
    const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    
    const allPrompts = useMemo(() => ASSESSMENT_DATA.flatMap(section => 
        section.prompts.map(p => ({...p, sectionTitle: section.title}))
    ), []);

    const currentPrompt = allPrompts[currentPromptIndex];
    const progress = ((currentPromptIndex + 1) / allPrompts.length) * 100;

    const handleNext = () => {
        if (currentPromptIndex < allPrompts.length - 1) {
            setCurrentPromptIndex(currentPromptIndex + 1);
        } else {
            onComplete(answers);
        }
    };

    const handleBack = () => {
        if (currentPromptIndex > 0) {
            setCurrentPromptIndex(currentPromptIndex - 1);
        }
    };

    const handleAnswer = (value) => {
        setAnswers({...answers, [currentPrompt.id]: value});
    };
    
    const sliderValue = answers[currentPrompt.id] || '5';

    return (
        <div className="flex-grow flex flex-col">
            <header className="p-4 flex items-center space-x-4 border-b border-gray-800">
                <button onClick={handleBack} className={`text-gray-400 hover:text-white ${currentPromptIndex === 0 ? 'opacity-0 cursor-default' : ''}`}>&larr; Back</button>
                <div className="w-full bg-gray-800 h-1 rounded-full">
                    <div className="bg-amber-500 h-1 rounded-full transition-all duration-300" style={{width: `${progress}%`}}></div>
                </div>
            </header>
            <div className="flex-grow flex flex-col p-6">
                <p className="text-sm font-bold text-amber-500 uppercase tracking-wider">{currentPrompt.sectionTitle}</p>
                <h2 className="text-2xl font-bold text-white my-4">{currentPrompt.text}</h2>
                
                {currentPrompt.type === 'slider' && (
                    <div className="mt-4">
                        <p className="text-center text-2xl font-bold text-white mb-2">{sliderValue}</p>
                        <input 
                            type="range" 
                            min="1" 
                            max="10" 
                            value={sliderValue}
                            onChange={(e) => handleAnswer(e.target.value)}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                )}
                {currentPrompt.type === 'textarea' && (
                    <textarea 
                        className="form-input flex-grow"
                        value={answers[currentPrompt.id] || ''}
                        onChange={(e) => handleAnswer(e.target.value)}
                        placeholder="Be honest with yourself..."
                    ></textarea>
                )}

                <button onClick={handleNext} className="btn btn-primary mt-6 w-full">
                    {currentPromptIndex === allPrompts.length - 1 ? 'See My Results' : 'Next'}
                </button>
            </div>
        </div>
    );
};

const ResultsView = ({ answers, onRestart, onSave }) => {
    const score = useMemo(() => {
        let total = 0;
        ASSESSMENT_DATA.forEach(section => {
            section.prompts.forEach(prompt => {
                const answer = answers[prompt.id];
                if (answer) {
                    total += prompt.points(answer);
                }
            });
        });
        return total;
    }, [answers]);

    const { status, description, plan } = useMemo(() => {
        if (score >= 45) {
            return { status: "Running on Fumes", description: "Right now, you're in survival mode. Your energy is low, the drains are high, and you're operating from a place of necessity. This is the hard part, but acknowledging it is the first step to rebuilding.", plan: ["Your immediate mission is to stop the bleeding. Use your answer for 'biggest drains' to identify ONE thing you can reduce your exposure to this week.", "Find your 'Blowtorch.' Look at your answer for what makes you feel powerful. Schedule just 15 minutes this week to do that one thing, with no other goal than to feel capable again.", "Acknowledge your 'Escape Hatch.' Knowing your old coping mechanisms is key to avoiding them. Your new plan starts with the healthy alternative you wrote down."] };
        } else if (score >= 20) {
            return { status: "Navigating the Fog", description: "You've made it through the worst of the storm, but the path forward isn't clear yet. You have moments of strength, but you're still weighed down by old patterns and fears. You're building, but the foundation is still settling.", plan: ["Focus on your 'Blowtorch.' You know what it is. The next step is to conquer the fear you wrote down. What's one small action that directly confronts that fear?", "Give your 'Quiet Kid' a voice. Take the letter you wrote and read it out loud to yourself. The first person who needs to hear your truth is you.", "Look at what you want 'more of.' Your one small, tangible action step is your mission for the next 7 days. It's non-negotiable."] };
        } else {
            return { status: "Building Momentum", description: "You're on the right track. You've identified your strengths, you know what you want, and you have a sense of your own power. The challenge now is consistency and turning your insights into lasting change.", plan: ["Your 'Action Step' is your primary focus. Who is the accountability partner you named? Reach out to them today and state your intention.", "Your healthy 'Coping Mechanism' needs to become a habit. How can you integrate it into your weekly routine, not just as a reaction to stress, but as a proactive practice?", "It's time to expand. You've defined your next path; now it's time to look at the one after that. What's the next big goal?"] };
        }
    }, [score]);

    return (
        <div className="flex-grow flex flex-col p-6 overflow-y-auto">
            <p className="text-sm font-bold text-amber-500 uppercase tracking-wider">Your Current Status</p>
            <h2 className="font-display font-black text-4xl text-white mt-2">{status}</h2>
            <p className="text-gray-400 mt-2">{description}</p>

            <div className="my-8">
                <h3 className="font-display font-bold text-2xl text-white border-b border-gray-700 pb-2 mb-4">Your Next Steps</h3>
                <ul className="space-y-4">
                    {plan.map((step, index) => (
                        <li key={index} className="flex items-start">
                            <span className="text-amber-500 font-bold text-xl mr-3 mt-1">&rarr;</span>
                            <p className="text-gray-300">{step}</p>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="mt-auto pt-6 space-y-3">
                 <button onClick={onSave} className="btn btn-primary w-full">Save My Results & Join the Community</button>
                 <button onClick={onRestart} className="btn bg-gray-800 text-gray-300 w-full">Take Assessment Again</button>
            </div>
        </div>
    );
};

const SaveView = ({ onCompleteSave, finalAnswers, status }) => {
    const [email, setEmail] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSaving(true);
        onCompleteSave(email, finalAnswers, status);
    };

    return (
        <div className="flex-grow flex flex-col justify-center p-8 text-center">
            <h2 className="font-display font-bold text-3xl text-white">Save Your Progress.</h2>
            <p className="text-gray-400 mt-2 mb-6">Enter your email to save your results and get updates from the "I'm Out Here" community. This is your first step to being in the camp.</p>
            <form onSubmit={handleSubmit}>
                <input 
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input text-center"
                    placeholder="your.email@example.com"
                    required
                />
                <button type="submit" className="btn btn-primary mt-4 w-full" disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save & Finish'}
                </button>
            </form>
        </div>
    );
};

const ThankYouView = ({ onRestart }) => (
     <div className="flex-grow flex flex-col justify-center items-center text-center p-8 animate-fadeIn">
        <h2 className="font-display font-bold text-4xl text-amber-500">You're In.</h2>
        <p className="text-gray-300 mt-2 mb-8">Thank you. Your results have been saved. Welcome to the community. Keep building your track.</p>
        <button onClick={onRestart} className="btn btn-primary">Start Over</button>
    </div>
);


// --- Main App Component ---
export default function App() {
    const [view, setView] = useState('welcome');
    const [finalAnswers, setFinalAnswers] = useState(null);
    const [finalStatus, setFinalStatus] = useState('');
    const [db, setDb] = useState(null);
    const [userId, setUserId] = useState(null);

    useEffect(() => {
        try {
            const app = initializeApp(firebaseConfig);
            const firestore = getFirestore(app);
            const auth = getAuth(app);
            setDb(firestore);
            onAuthStateChanged(auth, async (user) => {
                if (user) {
                    setUserId(user.uid);
                } else {
                    await signInAnonymously(auth);
                }
            });
        } catch (error) {
            console.error("Firebase init failed:", error);
        }
    }, []);

    const handleAssessmentComplete = (answers) => {
        const score = ASSESSMENT_DATA.flatMap(s => s.prompts).reduce((total, prompt) => {
            const answer = answers[prompt.id];
            return total + (answer ? prompt.points(answer) : 0);
        }, 0);
        
        let status;
        if (score >= 45) status = "Running on Fumes";
        else if (score >= 20) status = "Navigating the Fog";
        else status = "Building Momentum";

        setFinalAnswers(answers);
        setFinalStatus(status);
        setView('results');
    };
    
    const handleSaveRequest = () => {
        setView('save');
    };

    const handleCompleteSave = async (email, answers, status) => {
        if (!db || !userId) {
            alert("Error: Could not connect to save service. Please try again later.");
            return;
        }
        try {
            const docRef = doc(db, "assessments", userId);
            await setDoc(docRef, {
                email: email,
                status: status,
                answers: answers,
                completedAt: new Date().toISOString()
            });
            setView('thankyou');
        } catch (error) {
            console.error("Error saving assessment:", error);
            alert("There was a problem saving your results. Please try again.");
        }
    };

    const handleRestart = () => {
        setFinalAnswers(null);
        setFinalStatus('');
        setView('welcome');
    };

    const currentView = () => {
        switch (view) {
            case 'assessment':
                return <AssessmentView onComplete={handleAssessmentComplete} />;
            case 'results':
                return <ResultsView answers={finalAnswers} onRestart={handleRestart} onSave={handleSaveRequest} />;
            case 'save':
                return <SaveView onCompleteSave={handleCompleteSave} finalAnswers={finalAnswers} status={finalStatus} />;
            case 'thankyou':
                return <ThankYouView onRestart={handleRestart} />;
            case 'welcome':
            default:
                return <WelcomeView onBegin={() => setView('assessment')} />;
        }
    };

    return (
        <AppLayout>
            {currentView()}
            <style>{`
                .btn { font-weight: 700; padding: 0.75rem 1.5rem; border-radius: 0.5rem; transition: all 0.2s ease; text-transform: uppercase; letter-spacing: 0.05em; }
                .btn-primary { background-color: #eba23f; color: #111; }
                .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 4px 20px rgba(235, 162, 63, 0.25); }
                .form-input { background-color: #1a1a1a; border: 1px solid #333; border-radius: 0.5rem; padding: 1rem; width: 100%; color: #EAEAEA; transition: all 0.2s ease; }
                .form-input:focus { outline: none; border-color: #eba23f; box-shadow: 0 0 0 3px rgba(235, 162, 63, 0.3); }
                .animate-fadeIn { animation: fadeIn 0.6s ease-in-out; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </AppLayout>
    );
}
